const BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/api'  // Relative URL for production
  : 'http://localhost:5500/api';

// ========== BLOG FUNCTIONS ==========

export const fetchAllBlogs = async (page = 1, limit = 50, search = '') => {
  try {
    const queryParams = new URLSearchParams();
    
    // Only add params if they differ from defaults
    if (page !== 1) queryParams.set('page', page.toString());
    if (limit !== 50) queryParams.set('limit', limit.toString());
    if (search) queryParams.set('search', search);

    const url = queryParams.toString() 
      ? `${BASE_URL}/marathiblogs/blogs?${queryParams}`
      : `${BASE_URL}/marathiblogs/blogs`;
      
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error('Failed to fetch blogs');
    }
    
    const data = await response.json();
    
    // Return just the array for backward compatibility when using defaults
    if (page === 1 && limit === 50 && !search) {
      return data.data || [];
    }
    
    // Return pagination info when requested
    return {
      blogs: data.data || [],
      totalPages: data.totalPages || 1,
      currentPage: data.currentPage || 1,
      total: data.total || 0
    };
  } catch (error) {
    console.error('Error fetching blogs:', error);
    return page === 1 && limit === 50 && !search ? [] : {
      blogs: [],
      totalPages: 1,
      currentPage: 1,
      total: 0
    };
  }
};

// Fetch single blog by ID
export const fetchBlogById = async (id) => {
  try {
    const response = await fetch(`${BASE_URL}/marathiblogs/blogs/${id}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch blog');
    }
    
    const data = await response.json();
    return data.success ? data.data : null;
  } catch (error) {
    console.error('Error fetching blog:', error);
    return null;
  }
};

// Fetch featured blogs
export const fetchFeaturedBlogs = async () => {
  try {
    const response = await fetch(`${BASE_URL}/marathiblogs/blogs?featured=true&limit=3`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch featured blogs');
    }
    
    const data = await response.json();
    return data.data || [];
  } catch (error) {
    console.error('Error fetching featured blogs:', error);
    return [];
  }
};

// ========== ARTICLE FUNCTIONS ==========

// Fetch all articles with pagination
export const fetchAllArticles = async (page = 1, limit = 12, search = '') => {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search })
    });

    const response = await fetch(`${BASE_URL}/marathiblogs/articles?${queryParams}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch articles');
    }
    
    const data = await response.json();
    return {
      articles: data.data || [],
      totalPages: data.totalPages || 1,
      currentPage: data.currentPage || 1,
      total: data.total || 0
    };
  } catch (error) {
    console.error('Error fetching articles:', error);
    return {
      articles: [],
      totalPages: 1,
      currentPage: 1,
      total: 0
    };
  }
};

// Fetch single article by ID
export const fetchArticleById = async (id) => {
  try {
    const response = await fetch(`${BASE_URL}/marathiblogs/articles/${id}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch article');
    }
    
    const data = await response.json();
    return data.success ? data.data : null;
  } catch (error) {
    console.error('Error fetching article:', error);
    return null;
  }
};

// Fetch featured articles (latest articles as featured)
export const fetchFeaturedArticles = async (limit = 3) => {
  try {
    const response = await fetch(`${BASE_URL}/marathiblogs/articles?limit=${limit}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch featured articles');
    }
    
    const data = await response.json();
    return data.data || [];
  } catch (error) {
    console.error('Error fetching featured articles:', error);
    return [];
  }
};

// Fetch articles by tag
export const fetchArticlesByTag = async (tag, page = 1, limit = 12) => {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      search: tag // Using search to filter by tag
    });

    const response = await fetch(`${BASE_URL}/marathiblogs/articles?${queryParams}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch articles by tag');
    }
    
    const data = await response.json();
    return {
      articles: data.data || [],
      totalPages: data.totalPages || 1,
      currentPage: data.currentPage || 1,
      total: data.total || 0
    };
  } catch (error) {
    console.error('Error fetching articles by tag:', error);
    return {
      articles: [],
      totalPages: 1,
      currentPage: 1,
      total: 0
    };
  }
};

// Fetch articles by author
export const fetchArticlesByAuthor = async (author, page = 1, limit = 12) => {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      search: author // Using search to filter by author
    });

    const response = await fetch(`${BASE_URL}/marathiblogs/articles?${queryParams}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch articles by author');
    }
    
    const data = await response.json();
    return {
      articles: data.data || [],
      totalPages: data.totalPages || 1,
      currentPage: data.currentPage || 1,
      total: data.total || 0
    };
  } catch (error) {
    console.error('Error fetching articles by author:', error);
    return {
      articles: [],
      totalPages: 1,
      currentPage: 1,
      total: 0
    };
  }
};

// Search articles
export const searchArticles = async (searchQuery, page = 1, limit = 12) => {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      search: searchQuery
    });

    const response = await fetch(`${BASE_URL}/marathiblogs/articles?${queryParams}`);
    
    if (!response.ok) {
      throw new Error('Failed to search articles');
    }
    
    const data = await response.json();
    return {
      articles: data.data || [],
      totalPages: data.totalPages || 1,
      currentPage: data.currentPage || 1,
      total: data.total || 0
    };
  } catch (error) {
    console.error('Error searching articles:', error);
    return {
      articles: [],
      totalPages: 1,
      currentPage: 1,
      total: 0
    };
  }
};

// ========== INFOMATIC FUNCTIONS ==========

// Fetch all infomatics with optional pagination
export const fetchInfomatics = async (page = 1, limit = 50, search = '') => {
  try {
    const queryParams = new URLSearchParams();
    
    // Only add params if they differ from defaults
    if (page !== 1) queryParams.set('page', page.toString());
    if (limit !== 50) queryParams.set('limit', limit.toString());
    if (search) queryParams.set('search', search);

    const url = queryParams.toString() 
      ? `${BASE_URL}/marathiblogs/infomatics?${queryParams}`
      : `${BASE_URL}/marathiblogs/infomatics`;
      
    const response = await fetch(url);
    
    if (!response.ok) throw new Error('Failed to fetch infomatics');
    
    const data = await response.json();
    
    // Return just the array for backward compatibility when using defaults
    if (page === 1 && limit === 50 && !search) {
      return data.success ? data.data : [];
    }
    
    // Return pagination info when requested
    return {
      infomatics: data.data || [],
      totalPages: data.totalPages || 1,
      currentPage: data.currentPage || 1,
      total: data.total || 0
    };
  } catch (error) {
    console.error('Error fetching infomatics:', error);
    return page === 1 && limit === 50 && !search ? [] : {
      infomatics: [],
      totalPages: 1,
      currentPage: 1,
      total: 0
    };
  }
};

export const fetchInfomaticById = async (id) => {
  try {
    const response = await fetch(`${BASE_URL}/marathiblogs/infomatics/${id}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data.success ? data.data : null;
  } catch (error) {
    console.error('Error fetching infomatic:', error);
    return null;
  }
};

// ========== SPEECH FUNCTIONS ==========

// Fetch all speeches with optional pagination
export const fetchSpeeches = async (page = 1, limit = 50, search = '') => {
  try {
    const queryParams = new URLSearchParams();
    
    // Only add params if they differ from defaults
    if (page !== 1) queryParams.set('page', page.toString());
    if (limit !== 50) queryParams.set('limit', limit.toString());
    if (search) queryParams.set('search', search);

    const url = queryParams.toString() 
      ? `${BASE_URL}/marathiblogs/speeches?${queryParams}`
      : `${BASE_URL}/marathiblogs/speeches`;
      
    const response = await fetch(url);
    
    if (!response.ok) throw new Error('Failed to fetch speeches');
    
    const data = await response.json();
    
    // Return just the array for backward compatibility when using defaults
    if (page === 1 && limit === 50 && !search) {
      return data.data || [];
    }
    
    // Return pagination info when requested
    return {
      speeches: data.data || [],
      totalPages: data.totalPages || 1,
      currentPage: data.currentPage || 1,
      total: data.total || 0
    };
  } catch (error) {
    console.error('Error fetching speeches:', error);
    return page === 1 && limit === 50 && !search ? [] : {
      speeches: [],
      totalPages: 1,
      currentPage: 1,
      total: 0
    };
  }
};

// Fetch single speech by ID
export const fetchSpeechById = async (id) => {
  try {
    const response = await fetch(`${BASE_URL}/marathiblogs/speeches/${id}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch speech');
    }
    
    const data = await response.json();
    return data.success ? data.data : null;
  } catch (error) {
    console.error('Error fetching speech:', error);
    return null;
  }
};

// ========== GALLERY FUNCTIONS ==========

// Fetch all galleries
export const fetchAllGalleries = async () => {
  try {
    const response = await fetch(`${BASE_URL}/marathiblogs/gallery`);
    if (!response.ok) throw new Error('Failed to fetch galleries');
    const data = await response.json();
    return data.data || [];
  } catch (error) {
    console.error('Error fetching galleries:', error);
    return [];
  }
};

// Fetch featured galleries
export const fetchFeaturedGalleries = async () => {
  try {
    const response = await fetch(`${BASE_URL}/marathiblogs/gallery?featured=true&limit=3`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch featured galleries');
    }
    
    const data = await response.json();
    return data.data || [];
  } catch (error) {
    console.error('Error fetching featured galleries:', error);
    return [];
  }
};

// ========== VIDEO GALLERY FUNCTIONS ==========

// Fetch all video galleries with pagination and filters
export const fetchAllVideoGalleries = async (page = 1, limit = 12, filters = {}) => {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(filters.search && { search: filters.search }),
      ...(filters.feeling && { feeling: filters.feeling }),
      ...(filters.sort && { sort: filters.sort })
    });

    const response = await fetch(`${BASE_URL}/marathiblogs/videoGallery?${queryParams}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch video galleries');
    }
    
    return {
      videoGalleries: data.data || [],
      totalPages: data.totalPages || 1,
      currentPage: data.currentPage || 1,
      total: data.total || 0
    };
  } catch (error) {
    console.error('Error in fetchAllVideoGalleries:', error);
    throw error;
  }
};

// Fetch single video gallery by ID
export const fetchVideoGalleryById = async (id) => {
  try {
    const response = await fetch(`${BASE_URL}/marathiblogs/videoGallery/${id}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch video gallery');
    }
    
    const data = await response.json();
    return data.data || null;
  } catch (error) {
    console.error('Error fetching video gallery:', error);
    return null;
  }
};

// Fetch featured video galleries
export const fetchFeaturedVideoGalleries = async (limit = 6) => {
  try {
    const response = await fetch(
      `${BASE_URL}/marathiblogs/videoGallery?featured=true&limit=${limit}`
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch featured galleries');
    }
    
    return data.data || [];
  } catch (error) {
    console.error('Error in fetchFeaturedVideoGalleries:', error);
    throw error;
  }
};

// Search video galleries
export const searchVideoGalleries = async (searchQuery, page = 1, limit = 12) => {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      search: searchQuery
    });

    const response = await fetch(`${BASE_URL}/marathiblogs/videoGallery?${queryParams}`);
    
    if (!response.ok) {
      throw new Error('Failed to search video galleries');
    }
    
    const data = await response.json();
    return {
      videoGalleries: data.data || [],
      totalPages: data.totalPages || 1,
      currentPage: data.currentPage || 1,
      total: data.total || 0
    };
  } catch (error) {
    console.error('Error searching video galleries:', error);
    return {
      videoGalleries: [],
      totalPages: 1,
      currentPage: 1,
      total: 0
    };
  }
};

// Fetch video galleries by feeling
export const fetchVideoGalleriesByFeeling = async (feeling, page = 1, limit = 12) => {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      feeling: feeling
    });

    const response = await fetch(`${BASE_URL}/marathiblogs/videoGallery?${queryParams}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch video galleries by feeling');
    }
    
    const data = await response.json();
    return {
      videoGalleries: data.data || [],
      totalPages: data.totalPages || 1,
      currentPage: data.currentPage || 1,
      total: data.total || 0
    };
  } catch (error) {
    console.error('Error fetching video galleries by feeling:', error);
    return {
      videoGalleries: [],
      totalPages: 1,
      currentPage: 1,
      total: 0
    };
  }
};

// Fetch video galleries by hashtag
export const fetchVideoGalleriesByHashtag = async (hashtag, page = 1, limit = 12) => {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      hashtag: hashtag
    });

    const response = await fetch(`${BASE_URL}/marathiblogs/videoGallery?${queryParams}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch video galleries by hashtag');
    }
    
    const data = await response.json();
    return {
      videoGalleries: data.data || [],
      totalPages: data.totalPages || 1,
      currentPage: data.currentPage || 1,
      total: data.total || 0
    };
  } catch (error) {
    console.error('Error fetching video galleries by hashtag:', error);
    return {
      videoGalleries: [],
      totalPages: 1,
      currentPage: 1,
      total: 0
    };
  }
};

// ========== INTERVIEW FUNCTIONS ==========
export const fetchInterviews = async () => {
  try {
    const response = await fetch(`${BASE_URL}/marathiblogs/interviews`);
    if (!response.ok) throw new Error('Failed to fetch interviews');
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching interviews:', error);
    return [];
  }
};

// ========== QUOTE FUNCTIONS ==========
export const fetchQuotes = async () => {
  try {
    const response = await fetch(`${BASE_URL}/marathiblogs/quotes`);
    if (!response.ok) throw new Error('Failed to fetch quotes');
    const data = await response.json();
    return Array.isArray(data.data) ? data.data : [];
  } catch (error) {
    console.error('Error fetching quotes:', error);
    return [];
  }
};

// ========== HELPER FUNCTIONS ==========

// Helper function to get video thumbnail
export const getVideoThumbnail = (video) => {
  if (video.thumbnailUrl) {
    return getImageSrc(video.thumbnailUrl);
  }
  
  if (video.videoType === 'youtube' && video.videoUrl) {
    const videoId = extractYouTubeId(video.videoUrl);
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }
  }
  
  return '/api/placeholder/480/270';
};

// Helper function to extract YouTube video ID
export const extractYouTubeId = (url) => {
  if (!url) return null;
  
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  
  return (match && match[2].length === 11) ? match[2] : null;
};

// Helper function to get embed URL
export const getEmbedUrl = (video) => {
  if (!video.videoUrl) return null;
  
  switch (video.videoType) {
    case 'youtube':
      const youtubeId = extractYouTubeId(video.videoUrl);
      return youtubeId ? `https://www.youtube.com/embed/${youtubeId}` : null;
    
    case 'vimeo':
      const vimeoMatch = video.videoUrl.match(/vimeo\.com\/(\d+)/);
      return vimeoMatch ? `https://player.vimeo.com/video/${vimeoMatch[1]}` : null;
    
    default:
      return video.videoUrl;
  }
};

// Helper function to get video duration
export const getVideoDuration = (video) => {
  return '00:00';
};

// Helper function to format feeling in Marathi
export const formatFeelingInMarathi = (feeling) => {
  const feelingMap = {
    'happy': 'आनंदी',
    'excited': 'उत्साहित',
    'nostalgic': 'नॉस्टॅल्जिक',
    'proud': 'गर्विष्ठ',
    'thoughtful': 'विचारशील',
    'inspired': 'प्रेरित',
    'calm': 'शांत',
    'grateful': 'कृतज्ञ',
    'determined': 'दृढनिश्चयी',
    'hopeful': 'आशावादी',
    'other': 'इतर'
  };
  
  return feelingMap[feeling] || feeling;
};

// Helper function to get image source
export const getImageSrc = (thumbnail, baseUrl = BASE_URL) => {
  if (!thumbnail) return '/api/placeholder/400/240';
  if (thumbnail.startsWith('data:')) return thumbnail;
  if (thumbnail.startsWith('http')) return thumbnail;
  if (thumbnail.startsWith('uploads/')) {
    const serverUrl = baseUrl.replace('/api', '');
    return `${serverUrl}/${thumbnail}`;
  }
  return '/api/placeholder/400/240';
};

// Helper function to format date in Marathi
export const formatMarathiDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('mr-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Helper function to truncate content
export const truncateContent = (content, maxLength = 120) => {
  if (!content || !Array.isArray(content)) return '';
  const fullText = content.join(' ');
  return fullText.length > maxLength 
    ? fullText.substring(0, maxLength) + '...' 
    : fullText;
};

// Helper function to calculate read time
export const calculateReadTime = (content) => {
  if (!content || !Array.isArray(content)) return '१ मिनिट';
  
  const fullText = content.join(' ');
  const wordsPerMinute = 200;
  const words = fullText.split(' ').length;
  const minutes = Math.ceil(words / wordsPerMinute);
  
  if (minutes === 1) return '१ मिनिट';
  if (minutes <= 5) return `${minutes} मिनिटे`;
  return `${minutes} मिनिटे`;
};

// ========== SUBSCRIBER FUNCTIONS ==========
export const subscribeToMarathiContent = async (subscriberData) => {
  try {
    const response = await fetch(`${BASE_URL}/marathiblogs/subscribers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(subscriberData)
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to subscribe');
    }
    
    return {
      success: true,
      data: data.data,
      message: data.message
    };
  } catch (error) {
    console.error('Error subscribing:', error);
    return {
      success: false,
      message: error.message || 'Failed to subscribe'
    };
  }
};

// ========== CHATBOT FUNCTIONS ==========
export const sendChatMessageMarathi = async (message) => {
  try {
    const response = await fetch(`${BASE_URL}/chatbot/chat-marathi`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ message })
    });
    
    if (!response.ok) {
      throw new Error('Failed to send chat message');
    }
    
    const data = await response.json();
    return {
      success: true,
      data: data
    };
  } catch (error) {
    console.error('Error sending chat message:', error);
    throw error;
  }
};